import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Heart, Send, X, Loader2 } from "lucide-react";
import confetti from "canvas-confetti";
import api, { avatarSrc } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CJ_AVATAR_URL } from "../lib/constants";
import { toast } from "sonner";
import { playPointsSound } from "../lib/sound";
import { cjReact, cjBadgeBurst } from "../lib/cjCelebrate";

const TAB_LABELS = {
  feed: "Team feed",
  received: "I received",
  given: "I gave",
};

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    if (s < 604800) return `${Math.floor(s/86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function Kudos() {
  const { user } = useAuth();
  const [tab, setTab] = useState("feed");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/kudos", { params: { scope: tab } })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <Link to="/" data-testid="kudos-back" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink">
        <ArrowLeft size={14} /> Back to Hub
      </Link>

      <div className="mt-4 flex items-start gap-4 flex-wrap">
        <img src={CJ_AVATAR_URL} alt="CJ" className="h-16 w-16 rounded-full object-cover shadow-md ring-2 ring-white shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <div className="tc-eyebrow text-red-500">It's CJ!</div>
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">Kudos &amp; Wins</h1>
          <p className="text-slate-500 mt-1.5 text-sm max-w-xl">Hype your teammates. Each kudos = <strong>+5 points</strong> for the recipient. No daily cap. Pass it on.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="kudos-give-button"
          className="h-12 px-5 rounded-2xl text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
          style={{ background: "#ef4444" }}
        >
          <Heart size={16} fill="currentColor" /> Give kudos
        </button>
      </div>

      <div className="mt-7 flex gap-2 border-b border-slate-200">
        {Object.entries(TAB_LABELS).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`kudos-tab-${k}`}
            className={`px-4 h-11 font-bold text-sm -mb-px border-b-2 transition-all ${tab === k ? "border-red-500 text-tc-ink" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            {v}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3" data-testid="kudos-list">
        {loading ? (
          <div className="text-slate-400 py-10 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="tc-card p-10 text-center text-slate-500">
            <Heart size={36} className="mx-auto text-slate-300" />
            <div className="font-display text-2xl text-tc-ink mt-3">No kudos yet.</div>
            <div className="text-sm mt-1">
              {tab === "received"
                ? "Once a teammate hypes you up, it'll show here."
                : tab === "given"
                ? "Tap 'Give kudos' to start hyping someone."
                : "Nothing yet, be the first to hype a teammate."}
            </div>
          </div>
        ) : (
          items.map((k, i) => <KudosCard key={k.id} k={k} me={user.id} delay={i * 0.04} />)
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <GiveKudosModal
            onClose={() => setShowModal(false)}
            onSent={() => {
              setShowModal(false);
              load();
              playPointsSound();
              confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors: ["#ef4444","#f68b1f","#7ac143","#0091be","#ffd400"] });
            }}
            meId={user.id}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function KudosCard({ k, me, delay }) {
  const isMine = k.to_user_id === me;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
      className="tc-card p-4 sm:p-5 flex gap-4 items-start"
      style={{ borderLeft: `4px solid ${k.category_color || "#ef4444"}` }}
      data-testid={`kudos-item-${k.id}`}
    >
      <div className="flex items-center -space-x-2 shrink-0">
        <Avatar user={{ name: k.from_user_name, avatar_path: k.from_user_avatar_path, avatar_url: k.from_user_avatar_url }} size={40} />
        <Avatar user={{ name: k.to_user_name, avatar_path: k.to_user_avatar_path, avatar_url: k.to_user_avatar_url }} size={40} ring />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] leading-snug">
          <span className="font-bold text-tc-ink">{k.from_user_name}</span>
          <span className="text-slate-500"> sent kudos to </span>
          <span className="font-bold text-tc-ink">{isMine ? "you" : k.to_user_name}</span>
        </div>
        <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: `${k.category_color}22`, color: k.category_color }}>
          <span>{k.category_emoji}</span> {k.category_label}
          <span className="ml-1 text-slate-500 normal-case tracking-normal font-bold">· +{k.points}</span>
        </div>
        {k.note && <p className="mt-2 text-slate-700 italic leading-relaxed">"{k.note}"</p>}
        <div className="text-xs text-slate-400 mt-2 font-bold uppercase tracking-widest">{timeAgo(k.created_at)}</div>
      </div>
    </motion.div>
  );
}

function Avatar({ user, size = 36, ring = false }) {
  const src = avatarSrc(user);
  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size };
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={user?.name}
        onError={() => setBroken(true)}
        className={`rounded-full object-cover bg-slate-200 ${ring ? "ring-2 ring-white" : ""}`}
        style={style}
      />
    );
  }
  return <div className={`rounded-full bg-slate-200 text-slate-600 font-bold flex items-center justify-center ${ring ? "ring-2 ring-white" : ""}`} style={style}>{initial}</div>;
}

function GiveKudosModal({ onClose, onSent, meId }) {
  const [team, setTeam] = useState([]);
  const [cats, setCats] = useState([]);
  const [step, setStep] = useState(1);
  const [recipient, setRecipient] = useState(null);
  const [category, setCategory] = useState(null);
  const [customLabel, setCustomLabel] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/kudos/categories").catch(() => ({ data: [] })),
    ]).then(([{ data: u }, { data: c }]) => {
      // Anyone on the team (sales, admin, super_admin) can receive kudos — only
      // the current user is excluded so you can't kudos yourself.
      setTeam((Array.isArray(u) ? u : []).filter((x) => x.id !== meId));
      setCats(Array.isArray(c) ? c : []);
    });
  }, [meId]);

  const isOther = category?.key === "other";
  const trimmedCustom = customLabel.trim();
  const canAdvanceFromCategory = !!category && (!isOther || trimmedCustom.length > 0);
  const displayLabel = isOther ? (trimmedCustom || category?.label) : category?.label;

  const submit = async () => {
    if (!recipient || !category) return;
    if (isOther && !trimmedCustom) return;
    setSubmitting(true);
    try {
      const { data: result } = await api.post("/kudos", {
        to_user_id: recipient.id,
        category: category.key,
        note: note.trim() || null,
        custom_label: isOther ? trimmedCustom : null,
      });
      cjReact(`Kudos sent to ${recipient.name.split(" ")[0]}!`, {
        subtitle: `+5 points landed. Generosity points for you 💌`,
        intensity: "subtle",
        icon: category.emoji || "💝",
      });
      if (Array.isArray(result?.new_badges) && result.new_badges.length) {
        cjBadgeBurst(result.new_badges);
      }
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send kudos.");
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="kudos-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 30, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 200, damping: 22 }}
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-7 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} data-testid="kudos-modal-close" className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">
          <X size={18} />
        </button>

        <div className="tc-eyebrow text-red-500">Step {step} of 3</div>
        <h2 className="font-display text-2xl text-tc-ink mt-1">
          {step === 1 ? "Pick a teammate to hype" : step === 2 ? `What did ${recipient?.name.split(" ")[0]} do?` : "Add a quick note (optional)"}
        </h2>

        <div className="mt-5">
          {step === 1 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="kudos-recipient-grid">
              {team.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setRecipient(t)}
                  data-testid={`kudos-recipient-${t.id}`}
                  className={`p-3 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${recipient?.id === t.id ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <Avatar user={t} size={56} />
                  <div className="text-xs font-bold text-tc-ink text-center leading-tight line-clamp-2">{t.name.split(" ")[0]}</div>
                </button>
              ))}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-2" data-testid="kudos-category-list">
              {cats.map((c) => (
                <div key={c.key}>
                  <button
                    onClick={() => setCategory(c)}
                    data-testid={`kudos-category-${c.key}`}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left transition-all ${category?.key === c.key ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0" style={{ background: `${c.color}22` }}>
                      {c.emoji}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-tc-ink">{c.label}</div>
                    </div>
                    <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: c.color }}>+5</div>
                  </button>
                  {c.key === "other" && category?.key === "other" && (
                    <div className="mt-2 ml-2 pl-3 border-l-2 border-red-200">
                      <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Your reason</label>
                      <input
                        type="text"
                        value={customLabel}
                        onChange={(e) => setCustomLabel(e.target.value)}
                        maxLength={60}
                        placeholder="e.g. Went above and beyond on the demo"
                        data-testid="kudos-custom-label"
                        autoFocus
                        className="w-full rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none px-3 py-2.5 text-sm"
                      />
                      <div className="text-[11px] text-slate-400 text-right mt-1">{customLabel.length}/60</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {step === 3 && (
            <div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                maxLength={600}
                placeholder="Say a little more about why this kudos is deserved…"
                data-testid="kudos-note"
                className="w-full rounded-2xl border-2 border-slate-200 focus:border-red-500 outline-none p-3.5 text-sm leading-relaxed resize-none"
              />
              <div className="text-xs text-slate-400 text-right mt-1">{note.length}/600</div>
              <div className="mt-4 p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center gap-3">
                <Avatar user={recipient} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Sending to</div>
                  <div className="font-bold text-tc-ink truncate">{recipient?.name}</div>
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: `${category?.color}22`, color: category?.color }}>
                  <span>{category?.emoji}</span> {displayLabel}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-7 pt-4 border-t border-slate-100">
          <button
            onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
            className="px-4 h-11 rounded-xl font-bold text-slate-500 hover:bg-slate-100"
            data-testid="kudos-modal-back"
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !recipient) || (step === 2 && !canAdvanceFromCategory)}
              data-testid="kudos-modal-next"
              className="px-5 h-11 rounded-xl text-white font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: "#ef4444" }}
            >
              Next
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="kudos-modal-submit"
              className="px-5 h-11 rounded-xl text-white font-bold flex items-center gap-2 disabled:opacity-60"
              style={{ background: "#ef4444" }}
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending</> : <><Send size={14} /> Send kudos</>}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
